
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Platform, SafeAreaView, StatusBar, StyleSheet, Text, TextInput, TouchableOpacity, View, Modal, ScrollView, Linking, useWindowDimensions, Image } from "react-native";
import { useFonts, Montserrat_400Regular, Montserrat_600SemiBold, Montserrat_700Bold } from "@expo-google-fonts/montserrat";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from 'expo-image-picker';
import { StatusBar as ExpoStatusBar } from "expo-status-bar";
import './src/global.css';
import axios from 'axios'
import api, { setAuthToken, initAuthFromStorage, API_BASE } from './src/lib/api'
import { Ionicons } from '@expo/vector-icons';
// KYC (front/back -> single PDF per doc; send only id_doc + driver_license)
import * as Print from "expo-print";     


const COLORS = {
  primary: "#024A59",
  secondary: "#D99178",
  tertiary: "#49b6d6",
  text: "#0b1a1f",
  muted: "#6B7280",
  white: "#ffffff",
  bg: "#F6FAFB"
};

function Button({ title, onPress, disabled, style, variant="primary" }) {
  const bg = disabled ? "#9CA3AF" : (variant === "secondary" ? COLORS.secondary : (variant === "tertiary" ? COLORS.tertiary : COLORS.primary));
  const textColor = "#fff";
  return (
    <TouchableOpacity onPress={onPress} disabled={disabled} style={[styles.button, { backgroundColor: bg }, style]}>
      <Text style={[styles.buttonText, { color: textColor }]}>{title}</Text>
    </TouchableOpacity>
  );
}

function formatXAF(n) {
  if (n == null) return '0';
  try { return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }); }
  catch { return String(n); }
}

// -------- Install Prompt (Android + iOS + Fallback) ----------
function InstallPrompt({ visible, onClose, deferredEvent }) {
  const isIOS = Platform.OS === "ios" || (Platform.OS === "web" && /iPad|iPhone|iPod/.test(navigator.userAgent));
  const isAndroid = Platform.OS === "android" || (Platform.OS === "web" && /Android/i.test(navigator.userAgent));
  const canPrompt = !!deferredEvent;

  return (
    <Modal animationType="fade" transparent visible={visible}>
      <View style={styles.modalBackdrop}>
        <View style={styles.modalSheet}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 6 }}>
            <Image source={require("./assets/icons/chinda-512.png")} style={{ width: 35, height: 35, borderRadius: 6 }} />
            <Text style={styles.modalTitle}>Install Chindha</Text>
          </View>

          {isIOS ? (
            <Text style={styles.modalText}>
              On iPhone/iPad: Tap <Text style={{fontWeight: "700"}}>Share</Text> →{" "}
              <Text style={{fontWeight: "700"}}>Add to Home Screen</Text>.
            </Text>
          ) : (
            <>
              {canPrompt ? (
                <Text style={styles.modalText}>
                  Install Chindha on your device for faster access and offline support.
                </Text>
              ) : (
                <Text style={styles.modalText}>
                  To install: open the browser menu <Text style={{fontWeight:"700"}}>⋮</Text> and choose <Text style={{fontWeight:"700"}}>Install app</Text>.
                </Text>
              )}
              <Button
                title={canPrompt ? "Install now" : "OK"}
                onPress={() => {
                  if (deferredEvent) deferredEvent.prompt();
                  onClose();
                }}
                variant="secondary"
                style={{ marginTop: 12 }}
              />
            </>
          )}

          <TouchableOpacity onPress={onClose} style={{ marginTop: 16 }}>
            <Text style={[styles.modalText, {color: COLORS.primary, fontWeight: "600"}]}>Later</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

// -------------------- Screens -------------------------
function HomeScreen({ go }) {
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.white }}>
      <View style={styles.hero}>
        <View style={styles.heroTop}>
          <Text style={styles.heroBrand}>Chindha</Text>
          <Image source={require("./assets/icons/chinda-512.png")} style={{ width: 35, height: 35, borderRadius: 6, marginRight: 8 }} />
        </View>

        <View style={styles.heroCenter}>
          <Text style={styles.heroH1}>Fuel loan 5,000 frs</Text>
          <Text style={styles.heroSub}>Repay 5,350 frs in 10 days</Text>
        </View>

        <View style={styles.heroBottom}>
          <Button
            title="Get loan"
            onPress={go}
            variant="secondary"
          />
        </View>
      </View>
      <ExpoStatusBar style="light" />
    </SafeAreaView>
  );
}



// PDF-only doc picker row
// --- replace your DocRow with this ---
function DocRow({ label, value, onPick }) {
  return (
    <View style={{ marginTop: 12, width: "100%" }}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.docButton} onPress={onPick}>
        <Text style={[styles.docButtonText, {flex: 1}]} numberOfLines={1}>
          {value ? value.name : "Tap to upload (PDF only)"}
        </Text>
        <Text style={styles.docSmall}>Browse</Text>
      </TouchableOpacity>
      {value?.size && <Text style={styles.hint}>{Math.round(value.size/1024)} KB</Text>}
    </View>
  );
}


// Convert two images (front/back) into ONE PDF file-like object
async function convertImagesToPdf(frontImage, backImage, baseName) {
  if (!frontImage?.uri) throw new Error("Front image is required");

  if (Platform.OS === "web") {
    try {
      const { jsPDF } = await import("jspdf");
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const addImage = (uri) =>
        new Promise((resolve, reject) => {
          const img = new window.Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const r = Math.min(pageWidth / img.width, pageHeight / img.height);
              const w = img.width * r;
              const h = img.height * r;
              pdf.addImage(img, "JPEG", (pageWidth - w) / 2, (pageHeight - h) / 2, w, h);
              resolve();
            } catch (e) { reject(e); }
          };
          img.onerror = (e) => reject(e);
          img.src = uri;
        });

      await addImage(frontImage.uri);
      if (backImage?.uri) {
        pdf.addPage();
        await addImage(backImage.uri);
      }

      const blob = pdf.output("blob");
      const name = `${baseName}.pdf`;
      const file = new File([blob], name, { type: "application/pdf" });
      const uri = URL.createObjectURL(blob);
      return { uri, name, type: "application/pdf", file, size: blob.size };
    } catch (e) {
      console.warn("jsPDF failed, falling back to image: - App.js:182", e);
      return { uri: frontImage.uri, name: `${baseName}.jpg`, type: "image/jpeg" };
    }
  } else {
    // Native: simple HTML -> PDF with expo-print
    const html = `
      <html>
        <head><meta name="viewport" content="width=device-width, initial-scale=1.0"/></head>
        <body style="margin:0;padding:0;">
          <div style="display:flex;flex-direction:column;gap:16px;align-items:center;justify-content:center;">
            <img src="${frontImage.uri}" style="width:100%;height:auto;"/>
            ${backImage?.uri ? `<img src="${backImage.uri}" style="width:100%;height:auto;"/>` : ""}
          </div>
        </body>
      </html>
    `;
    const { uri } = await Print.printToFileAsync({ html });
    return { uri, name: `${baseName}.pdf`, type: "application/pdf" };
  }
}

// Append to FormData (web + native)
async function appendBinary(fd, field, fileLike, fallbackName) {
  if (!fileLike?.uri) throw new Error(`${field} is missing`);
  const name = fileLike.name || fallbackName || "file.bin";
  const type = fileLike.type || fileLike.mimeType || "application/octet-stream";

  if (Platform.OS === "web") {
    const res = await fetch(fileLike.uri);
    const blob = await res.blob();
    const f = new File([blob], name, { type: type || blob.type || "application/octet-stream" });
    fd.append(field, f, name);
  } else {
    fd.append(field, { uri: fileLike.uri, name, type });
  }
}

// KYC SCREEN — no extra modal, direct pickers on tap
function KycScreen({ go, setProfile }) {
  const [momoName, setMomoName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [network, setNetwork] = useState("MTN");
  const [refCode, setRefCode] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Mode per doc: 'photo' (front+back) or 'document' (single PDF)
  const [idDocType, setIdDocType] = useState("photo");
  const [licenseDocType, setLicenseDocType] = useState("photo");

  // If doc=PDF
  const [idDocument, setIdDocument] = useState(null);
  const [licenseDocument, setLicenseDocument] = useState(null);

  // If doc=photos
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);

  // Password eyes
  const [loginVisible, setLoginVisible] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Eye toggles
  const [showPwd, setShowPwd] = useState(false);
  const [showLoginPwd, setShowLoginPwd] = useState(false);

  const phoneErr = useMemo(() => validateCameroonPhone(phone, network), [phone, network]);

  const formOk = useMemo(() => {
    const basicOk =
      momoName && phone && !phoneErr &&
      password && password.length >= 6 &&
      refCode && accepted;

    if (!basicOk) return false;

    const idOk = idDocType === "document" ? !!idDocument : !!(idFront && idBack);
    const licOk = licenseDocType === "document" ? !!licenseDocument : !!(licenseFront && licenseBack);
    return idOk && licOk;
  }, [momoName, phone, phoneErr, password, refCode, accepted, idDocType, idDocument, idFront, idBack, licenseDocType, licenseDocument, licenseFront, licenseBack]);

  // Pickers (direct — no extra modal)
  async function pickDocument(onSet) {
    try {
      const res = await DocumentPicker.getDocumentAsync({ multiple: false, copyToCacheDirectory: true, type: ["application/pdf"] });
      if (res.canceled) return;
      const file = res.assets?.[0];
      if (!file) return;
      if (file.size && file.size > 10 * 1024 * 1024) return alert("PDF must be < 10MB");
      onSet(file);
    } catch (e) {
      console.error("pickDocument error - App.js:279", e);
      alert("Error selecting document");
    }
  }

  async function pickImageDirect(onSet) {
    try {
      if (Platform.OS !== "web") {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== "granted") return alert("Permission required to access photos");
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 0.85,
      });
      if (result.canceled || !result.assets?.[0]) return;

      const a = result.assets[0];
      const file = {
        uri: a.uri,
        name: a.fileName || `photo_${Date.now()}.jpg`,
        type: a.type?.includes("image") ? a.type : "image/jpeg",
        size: a.fileSize || 0,
        mimeType: "image/jpeg",
      };
      onSet(file);
    } catch (e) {
      console.error("pickImageDirect error - App.js:308", e);
      alert("Error picking image");
    }
  }

  function clearIdDocuments() { setIdDocument(null); setIdFront(null); setIdBack(null); }
  function clearLicenseDocuments() { setLicenseDocument(null); setLicenseFront(null); setLicenseBack(null); }

  // SUBMIT — build two PDFs (if on photos mode) and send only id_doc + driver_license
  async function submitKyc() {
    if (!formOk) return;
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("full_name", momoName.trim());
      fd.append("phone_e164", phone.trim());
      fd.append("momo_msisdn", phone.trim());
      fd.append("momo_network", network.toUpperCase());
      fd.append("password", password);
      fd.append("accepted_terms_version", "1");
      fd.append("ref_code", refCode.trim());

      // ID
      if (idDocType === "document") {
        await appendBinary(fd, "id_doc", idDocument, "id_document.pdf");
      } else {
        const idPdf = await convertImagesToPdf(idFront, idBack, "id_document");
        await appendBinary(fd, "id_doc", idPdf, idPdf.name || "id_document.pdf");
      }

      // Driver's License
      if (licenseDocType === "document") {
        await appendBinary(fd, "driver_license", licenseDocument, "drivers_license.pdf");
      } else {
        const dlPdf = await convertImagesToPdf(licenseFront, licenseBack, "drivers_license");
        await appendBinary(fd, "driver_license", dlPdf, dlPdf.name || "drivers_license.pdf");
      }

      // Let axios set boundary; do NOT set Content-Type manually.
      const { data } = await axios.post(`${API_BASE}/api/auth/register`, fd, {
        headers: { Authorization: api.defaults.headers.common.Authorization || "" },
      });

      if (data?.token) setAuthToken(data.token);
      setProfile({ momoName, phone, network, refCode });
      go("awaiting");
    } catch (e) {
      console.error("KYC submission error - App.js:356", e);
      alert(e?.response?.data?.error || e.message || "KYC submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  // Small UI bits
  function DocTypeSelector({ docType, onTypeChange, label }) {
    return (
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.label}>{label}</Text>
        <View style={styles.segment}>
          <TouchableOpacity onPress={() => onTypeChange("photo")} style={[styles.segItem, docType === "photo" && styles.segItemActive]}>
            <Text style={[styles.segText, docType === "photo" && styles.segTextActive]}>📷 Take Photos</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onTypeChange("document")} style={[styles.segItem, docType === "document" && styles.segItemActive]}>
            <Text style={[styles.segText, docType === "document" && styles.segTextActive]}>📄 Upload PDF</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  function DocumentUpload({ label, document, onPick, docType }) {
    return (
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.label}>{label}</Text>
        <TouchableOpacity style={styles.docButton} onPress={onPick}>
          <Ionicons name="document-text" size={20} color={COLORS.tertiary} />
          <Text style={[styles.docButtonText, { flex: 1, marginLeft: 8 }]} numberOfLines={1}>
            {document ? document.name : `Tap to upload ${docType === "id" ? "ID" : "license"} PDF`}
          </Text>
          <Text style={styles.docSmall}>Browse</Text>
        </TouchableOpacity>
        {document && <Text style={[styles.hint, { fontSize: 12, marginTop: 4 }]}>{Math.round((document.size || 0) / 1024)} KB • PDF</Text>}
      </View>
    );
  }

  function PhotoUpload({ label, frontImage, backImage, onPickFront, onPickBack, docType }) {
    return (
      <View style={{ marginBottom: 16 }}>
        <Text style={styles.label}>{label}</Text>
        <View style={{ flexDirection: "row", gap: 12, marginBottom: 8 }}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.hint, { marginBottom: 6, fontSize: 14 }]}>Front Side</Text>
            <TouchableOpacity style={styles.docButton} onPress={onPickFront}>
              <Ionicons name="camera" size={20} color={COLORS.tertiary} />
              <Text style={[styles.docButtonText, { flex: 1, marginLeft: 8 }]} numberOfLines={1}>
                {frontImage ? frontImage.name : "Tap to upload"}
              </Text>
              <Text style={styles.docSmall}>Choose</Text>
            </TouchableOpacity>
            {frontImage && <Text style={[styles.hint, { fontSize: 12, marginTop: 4 }]}>{Math.round((frontImage.size || 0) / 1024)} KB • Photo</Text>}
          </View>

          <View style={{ flex: 1 }}>
            <Text style={[styles.hint, { marginBottom: 6, fontSize: 14 }]}>Back Side</Text>
            <TouchableOpacity style={styles.docButton} onPress={onPickBack}>
              <Ionicons name="camera" size={20} color={COLORS.tertiary} />
              <Text style={[styles.docButtonText, { flex: 1, marginLeft: 8 }]} numberOfLines={1}>
                {backImage ? backImage.name : "Tap to upload"}
              </Text>
              <Text style={styles.docSmall}>Choose</Text>
            </TouchableOpacity>
            {backImage && <Text style={[styles.hint, { fontSize: 12, marginTop: 4 }]}>{Math.round((backImage.size || 0) / 1024)} KB • Photo</Text>}
          </View>
        </View>
        <Text style={[styles.hint, { fontSize: 12 }]}>
          Take clear photos of both sides of your {docType === "id" ? "ID card" : "driver's license"}.
        </Text>
      </View>
    );
  }

    // --- Login existing account (from the modal) ---
  async function loginExisting() {
    setLoginLoading(true);
    setLoginError('');
    try {
      const { data } = await axios.post(`${API_BASE}/api/auth/login`, {
        phone_e164: loginPhone.trim(),
        password: loginPassword
      });
      if (!data?.token) throw new Error('No token returned');
      setAuthToken(data.token);
      setLoginVisible(false);
      setLoginPhone(''); setLoginPassword('');
      go('dashboard'); // go straight to dashboard
    } catch (e) {
      setLoginError(e?.response?.data?.error || e.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  }
  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.bg }}>
      <ScrollView contentContainerStyle={styles.centeredScroll}>
        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>One-time Identity verification</Text>

          <LabeledInput label="MoMo/OM account name" value={momoName} onChangeText={setMomoName} placeholder="Exact name on your MoMo/OM SIM" />
          <Text style={[styles.hint, { marginTop: 4 }]}>
            Must match the name registered on your MTN MoMo or Orange Money account.
          </Text>

          <View style={{ marginTop: 12, width: "100%" }}>
            <Text style={styles.label}>Wallet network</Text>
            <View style={styles.segment}>
              <TouchableOpacity onPress={() => setNetwork("MTN")} style={[styles.segItem, network === "MTN" && styles.segItemActive]}>
                <Text style={[styles.segText, network === "MTN" && styles.segTextActive]}>MTN</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setNetwork("Orange")} style={[styles.segItem, network === "Orange" && styles.segItemActive]}>
                <Text style={[styles.segText, network === "Orange" && styles.segTextActive]}>Orange</Text>
              </TouchableOpacity>
            </View>
          </View>

          <LabeledInput label="MoMo/OM Phone Number (to receive funds)" value={phone} onChangeText={setPhone} placeholder="+237 6XX XXX XXX" />
          {!!phoneErr && <Text style={styles.error}>{phoneErr}</Text>}

          <View style={{ marginTop: 12, width: "100%" }}>
            <Text style={styles.label}>Password</Text>
            <View style={{ position: "relative" }}>
              <TextInput
                style={styles.input}
                value={password}
                onChangeText={setPassword}
                placeholder="•••••••• (min 6 chars)"
                secureTextEntry={!showPwd}
              />
              <TouchableOpacity
                onPress={() => setShowPwd((s) => !s)}
                style={{ position: "absolute", right: 12, top: 12, padding: 4 }}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                accessibilityLabel={showPwd ? "Hide password" : "Show password"}
              >
                <Ionicons name={showPwd ? "eye-off" : "eye"} size={20} color={COLORS.muted} />
              </TouchableOpacity>
            </View>
          </View>

          {/* ID */}
          <DocTypeSelector docType={idDocType} onTypeChange={(t) => { setIdDocType(t); clearIdDocuments(); }} label="ID Card Verification Method" />
          {idDocType === "document" ? (
            <DocumentUpload label="ID Card (PDF)" document={idDocument} onPick={() => pickDocument(setIdDocument)} docType="id" />
          ) : (
            <PhotoUpload
              label="ID Card Photos"
              frontImage={idFront}
              backImage={idBack}
              onPickFront={() => pickImageDirect(setIdFront)}
              onPickBack={() => pickImageDirect(setIdBack)}
              docType="id"
            />
          )}

          {/* Driver's License */}
          <DocTypeSelector docType={licenseDocType} onTypeChange={(t) => { setLicenseDocType(t); clearLicenseDocuments(); }} label="Driver's License Verification Method" />
          {licenseDocType === "document" ? (
            <DocumentUpload label="Driver's License (PDF)" document={licenseDocument} onPick={() => pickDocument(setLicenseDocument)} docType="license" />
          ) : (
            <PhotoUpload
              label="Driver's License Photos"
              frontImage={licenseFront}
              backImage={licenseBack}
              onPickFront={() => pickImageDirect(setLicenseFront)}
              onPickBack={() => pickImageDirect(setLicenseBack)}
              docType="license"
            />
          )}

          <LabeledInput label="Referrer's code" value={refCode} onChangeText={setRefCode} placeholder="e.g., CHD1234" />

          <TouchableOpacity onPress={() => setAccepted((v) => !v)} style={styles.checkRow}>
            <View style={[styles.checkbox, accepted && { backgroundColor: COLORS.tertiary, borderColor: COLORS.tertiary }]} />
            <Text style={[styles.hint, { flex: 1 }]}>
              I accept the <Text style={{ color: COLORS.tertiary }} onPress={() => Linking.openURL("#")}>Terms & Conditions</Text>.
            </Text>
          </TouchableOpacity>

          <Button
            title={submitting ? "Submitting…" : "Submit for review"}
            onPress={submitKyc}
            disabled={!formOk || submitting}
            variant="secondary"
            style={{ marginTop: 14, width: "100%" }}
          />
        </View>
        <TouchableOpacity onPress={() => setLoginVisible(true)} style={{ marginTop: 12 }}>
          <Text style={[styles.hint, { textAlign: 'center' }]}>
            Already have an account? <Text style={{ color: COLORS.tertiary, fontFamily: 'Montserrat_600SemiBold' }}>Log in</Text>
          </Text>
        </TouchableOpacity>
      </ScrollView>
      {/* Login modal */}
      <Modal transparent visible={loginVisible} animationType="fade">
         <View style={styles.modalBackdrop}>
           <View style={styles.modalSheet}>
             <Text style={styles.modalTitle}>Log in</Text>

             <LabeledInput
              label="Phone (E.164)"
              value={loginPhone}
              onChangeText={setLoginPhone}
              placeholder="+2376XXXXXXXX"
            />
            {/* Login password with eye toggle */}
            <View style={{ marginTop: 12, width: '100%' }}>
              <Text style={styles.label}>Password</Text>
              <View style={{ position: 'relative' }}>
                <TextInput
                  style={styles.input}
                  value={loginPassword}
                  onChangeText={setLoginPassword}
                  placeholder="••••••••"
                  secureTextEntry={!showLoginPwd}
                />
                <TouchableOpacity
                  onPress={() => setShowLoginPwd(s => !s)}
                  style={{ position: 'absolute', right: 12, top: 12, padding: 4 }}
                  hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  accessibilityLabel={showLoginPwd ? "Hide password" : "Show password"}
                >
                  <Ionicons name={showLoginPwd ? 'eye-off' : 'eye'} size={20} color={COLORS.muted} />
                </TouchableOpacity>
              </View>
            </View>
            {!!loginError && <Text style={styles.error}>{loginError}</Text>}

            <View style={{ flexDirection: 'row', gap: 12, marginTop: 12 }}>
              <View style={{ flex: 1 }}>
                <Button title="Cancel" onPress={() => { setLoginVisible(false); setLoginError(''); }} variant="tertiary" />
              </View>
              <View style={{ flex: 1 }}>
                <Button
                  title={loginLoading ? "Logging in…" : "Log in"}
                  onPress={loginExisting}
                  disabled={loginLoading || !loginPhone || !loginPassword}
                  variant="secondary"
                />
              </View>
            </View>
          </View>
        </View>
      </Modal>
      <ExpoStatusBar style="dark" />
    </SafeAreaView>
  );
}


function sleep(ms){ return new Promise(r => setTimeout(r, ms)) }



// function sleep(ms){ return new Promise(r => setTimeout(r, ms)) }

function AwaitingScreen({ go }) {
  const [phase, setPhase] = useState('kyc') // 'kyc' | 'loan'
  const [message, setMessage] = useState('We are verifying your account…')
  const requestedRef = useRef(false)
  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false
    ;(async () => {
      // 1) Wait for verification
      setPhase('kyc')
      setMessage('Currently verifying your information. Please do not close this page.')
      for (;;) {
        if (cancelled.current) return
        try {
          const { data } = await api.get('/api/users/verification')
          if (data?.status === 'rejected') {
            setMessage(`Your account was rejected: ${data?.reason || 'no reason provided'}`)
            return
          }
          if (data?.verified) break
        } catch {}
        await sleep(5000)
      }

      // 2) Auto request a loan exactly once
      setPhase('loan')
      setMessage('Identity verified. Requesting your loan…')
      if (!requestedRef.current) {
        try {
          await api.post('/api/loans/request', { amount: 5000, term_days: 10, description: 'Chinda loan payout' })
          requestedRef.current = true
        } catch (e) {
          // Even if this fails, we'll still poll status in case backend created it.
          setMessage('Could not request loan automatically. Waiting for approval if one exists…')
        }
      }

      // 3) Poll loan request status
      setMessage('Waiting for admin approval of your loan. Please keep this page open.')
      for (;;) {
        if (cancelled.current) return
        try {
          const { data } = await api.get('/api/loans/my/request/status')
          if (data?.status === 'approved') {
            go('dashboard')
            return
          }
          if (data?.status === 'rejected') {
            setMessage('Your loan request was rejected.')
            return
          }
        } catch {}
        await sleep(5000)
      }
    })()
    return () => { cancelled.current = true }
  }, [go])

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.white }}>
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Text style={styles.sectionTitle}>
          {phase === 'kyc' ? 'Verifying your account…' : 'Awaiting loan approval…'}
        </Text>
        <Text style={styles.hint}>{message}</Text>
      </View>
      <ExpoStatusBar style="auto" />
    </SafeAreaView>
  )
}



/** NEW: AwaitingLoanScreen — used when requesting from Dashboard (KYC already done) */
function AwaitingLoanScreen({ go }) {
  const [message, setMessage] = useState('Waiting for admin approval of your loan. Please keep this page open.')
  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false
    ;(async () => {
      for (;;) {
        if (cancelled.current) return
        try {
          const { data } = await api.get('/api/loans/my/request/status')
          if (data?.status === 'approved') { go('dashboard'); return }
          if (data?.status === 'rejected') { setMessage('Your loan request was rejected.'); return }
        } catch {}
        await sleep(5000)
      }
    })()
    return () => { cancelled.current = true }
  }, [go])

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.white }}>
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 24 }}>
        <Text style={styles.sectionTitle}>Awaiting loan approval…</Text>
        <Text style={styles.hint}>{message}</Text>
      </View>
      <ExpoStatusBar style="auto" />
    </SafeAreaView>
  )
}

function DashboardScreen({ go, setAuthed, setScreen }) {
  const [loanSummary, setLoanSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [user, setUser] = useState(null)

  // Repay modal state
  const [repayVisible, setRepayVisible] = useState(false)
  const [payType, setPayType] = useState('full') // 'full' | 'installment' | 'custom'
  const [customAmount, setCustomAmount] = useState('')
  const [payMethod, setPayMethod] = useState('MTN') // 'MTN' | 'ORANGE'
  const [submitting, setSubmitting] = useState(false)

  // NEW: request-loan button state
  const [requestingLoan, setRequestingLoan] = useState(false)

  // NEW: Logout modal state
  const [logoutVisible, setLogoutVisible] = useState(false)

  const pollRef = useRef(null)

  async function load() {
    setLoading(true); setError('')
    try {
      const me = await api.get('/api/users/me').then(r => r.data).catch(() => null)
      setUser(me || null)
      if (me?.momo_network) setPayMethod(String(me.momo_network).toUpperCase())

      const data = await api.get('/api/loans/active').then(r => r.data)
      setLoanSummary(data)
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load dashboard')
    } finally {
      setLoading(false)
    }
  }

  // useEffect(() => {
  //   load()
  //   return () => {
  //     if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  //   }
  // }, [])

    useEffect(() => {
    (async () => {
      await load()
      // Ask backend to immediately poll this user’s pending MoMo transactions
      try {
        const r = await api.post('/api/momo/poll/me-pending')
        // if anything changed, reload summary
        if (r?.data?.updated > 0) {
          await load()
        }
      } catch {}
    })()

    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [])


  // NEW: Logout function
  const handleLogout = () => {
    // Clear token from storage and axios
    localStorage.removeItem('token');
    delete api.defaults.headers.common.Authorization;
    
    // Reset app state
    setLogoutVisible(false);
    setAuthed(false);
    setScreen('home');
  };

  const loan = loanSummary?.loan || null
  const outstanding = loanSummary?.outstanding || 0
  const repaid = loanSummary?.repaid || 0
  const txns = loanSummary?.transactions || []
  const daysLeft = (() => {
    if (!loan?.due_at) return null
    const due = new Date(loan.due_at)
    const now = new Date()
    const diff = Math.ceil((due - now) / (1000*60*60*24))
    return diff < 0 ? 0 : diff
  })()
  const suggestedInstallment = Math.min(
    outstanding,
    Math.round((loan?.total_due || 0) * 0.10)
  )

  function chosenAmount() {
    if (!loan) return 0
    if (payType === 'full') return outstanding
    if (payType === 'installment') return suggestedInstallment
    const n = Number(customAmount)
    return Number.isFinite(n) ? n : 0
  }

  async function initiateRepayment() {
    if (!loan) return
    const amount = chosenAmount()
    if (!amount || amount <= 0) return alert('Enter a valid amount')
    if (amount > outstanding) return alert('Amount exceeds outstanding')

    setSubmitting(true)
    try {
      await api.post(`/api/loans/${loan.id}/repayments/initiate`, {
        amount,
        paymentMethod: payMethod
      })

      // Close modal, start short polling to refresh until we see status change
      setRepayVisible(false)
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(async () => {
        try {
          const data = await api.get('/api/loans/active').then(r => r.data)
          setLoanSummary(data)
          // stop polling if outstanding decreased
          if ((data?.outstanding ?? outstanding) < outstanding) {
            clearInterval(pollRef.current); pollRef.current = null
          }
        } catch {}
      }, 5000)
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Failed to initiate repayment')
    } finally {
      setSubmitting(false)
    }
  }

  // NEW: request a new loan directly from the dashboard (no KYC redirect)
  async function requestLoan() {
    if (loan) return
    setRequestingLoan(true)
    try {
      await api.post('/api/loans/request', {
        amount: 5000,
        term_days: 10,
        description: 'Chinda loan payout'
      })
      // Go to "waiting for approval" screen
      go('awaiting_loan')
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Failed to request loan')
    } finally {
      setRequestingLoan(false)
    }
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.bg }}>
      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 160, alignItems: "center" }}>
        <View style={styles.topBar}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Image source={require("./assets/icons/chinda-512.png")} style={{ width: 35, height: 35, borderRadius: 6 }} />
            <Text style={styles.brand}>Chindha</Text>
          </View>
          
          {/* NEW: Logout button in top right */}
          <TouchableOpacity 
            onPress={() => setLogoutVisible(true)}
            style={styles.logoutButton}
          >
            <Ionicons name="log-out-outline" size={24} color={COLORS.primary} />
          </TouchableOpacity>
        </View>

        <Text style={[styles.sectionTitle, { alignSelf: "center" }]}>My Portfolio</Text>

        <View style={{ width: "100%", maxWidth: 700, gap: 12 }}>
          <View style={[styles.card, { backgroundColor: COLORS.primary }]}>
            {!!user?.full_name && (
              <Text style={{ fontFamily:'Montserrat_600SemiBold', color: COLORS.text }}>
                Hi, {String(user.full_name).split(' ')[0]}
              </Text>
            )}
            <Text style={styles.cardTitleLight}>
              {loading ? 'Loading…' : loan ? `Loan #${loan.id}` : 'No active loan'}
            </Text>

            {error ? (
              <Text style={styles.hintLight}>{error}</Text>
            ) : loan ? (
              <>
                <Text style={styles.outstandingAmt}>{formatXAF(outstanding)} XAF</Text>
                <Text style={styles.hintLight}>
                  Outstanding • {loan.due_at ? `Due in ${daysLeft} day${daysLeft===1?'':'s'}` : '—'}
                </Text>
                <Text style={[styles.hintLight, { marginTop: 6 }]}>
                  Repaid so far: {formatXAF(repaid)} XAF • Total: {formatXAF(loan.total_due)} XAF
                </Text>
              </>
            ) : (
              <Text style={styles.hintLight}>You can request a new loan.</Text>
            )}
          </View>

          {/* Transactions */}
          <View style={[styles.card, { backgroundColor: '#fff' }]}>
            <Text style={[styles.sectionTitle, { marginBottom: 4 }]}>Recent transactions</Text>
            {txns.length === 0 ? (
              <Text style={styles.hint}>No transactions yet.</Text>
            ) : (
              txns.slice(0, 8).map(t => (
                <View key={t.id} style={{ paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' }}>
                  <Text style={{ fontFamily: 'Montserrat_600SemiBold', color: COLORS.text }}>
                    {t.purpose === 'repayment' ? 'Repayment' : t.purpose} • {t.status.toUpperCase()}
                  </Text>
                  <Text style={styles.hint}>
                    {formatXAF(t.amount)} XAF • {new Date(t.initiated_at).toLocaleString()}
                  </Text>
                </View>
              ))
            )}
          </View>
        </View>
      </ScrollView>

      {/* Bottom CTA: Request/Repay */}
      <View style={styles.bottomCta}>
        {loan ? (
          <View style={{ width: '100%', maxWidth: 700, flexDirection: 'row', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <Button
                title="Repay loan"
                onPress={() => {
                  setPayType('full')
                  setCustomAmount('')
                  setPayMethod((user?.momo_network || 'MTN').toUpperCase())
                  setRepayVisible(true)
                }}
                variant="tertiary"
                // disabled={true}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Button title="Request loan" onPress={() => {}} disabled variant="tertiary" />
            </View>
          </View>
        ) : (
          <Button
            title={requestingLoan ? "Requesting…" : "Request loan"}
            onPress={requestLoan}
            disabled={requestingLoan}
            variant="tertiary"
          />
        )}
        <Text style={[styles.hint, { textAlign: "center", marginTop: 6 }]}>
          {loan ? 'You have an ongoing loan.' : 'No active loan.'}
        </Text>
      </View>

      {/* Repay modal */}
      <Modal transparent visible={repayVisible} animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalSheet}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={styles.modalTitle}>Repay loan</Text>
              <TouchableOpacity onPress={() => setRepayVisible(false)} style={{ padding: 4 }}>
                <Ionicons name="close" size={22} color={COLORS.muted} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.hint, { marginTop: 6 }]}>
              Outstanding: {formatXAF(outstanding)} XAF
            </Text>

            {/* Payment method */}
            <View style={{ marginTop: 12 }}>
              <Text style={styles.label}>Payment method</Text>
              <View style={styles.segment}>
                <TouchableOpacity onPress={() => setPayMethod('MTN')} style={[styles.segItem, payMethod === 'MTN' && styles.segItemActive]}>
                  <Text style={[styles.segText, payMethod === 'MTN' && styles.segTextActive]}>MTN</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => setPayMethod('ORANGE')} style={[styles.segItem, payMethod === 'ORANGE' && styles.segItemActive]}>
                  <Text style={[styles.segText, payMethod === 'ORANGE' && styles.segTextActive]}>ORANGE</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Amount options */}
            <View style={{ marginTop: 12 }}>
              <Text style={styles.label}>Choose amount</Text>

              <TouchableOpacity onPress={() => setPayType('full')} style={[styles.segItem, { borderWidth: 1, borderColor: '#e5e7eb', marginTop: 4, borderRadius: 10, paddingVertical: 10, backgroundColor: payType==='full' ? '#e0f2fe' : '#fff' }]}>
                <Text style={{ fontFamily: 'Montserrat_600SemiBold', color: COLORS.text }}>
                  Full amount — {formatXAF(outstanding)} XAF
                </Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={() => setPayType('installment')} style={[styles.segItem, { borderWidth: 1, borderColor: '#e5e7eb', marginTop: 8, borderRadius: 10, paddingVertical: 10, backgroundColor: payType==='installment' ? '#e0f2fe' : '#fff' }]}>
                <Text style={{ fontFamily: 'Montserrat_600SemiBold', color: COLORS.text }}>
                  Installment (10%) — {formatXAF(suggestedInstallment)} XAF
                </Text>
              </TouchableOpacity>

              <View style={{ marginTop: 8 }}>
                <TouchableOpacity onPress={() => setPayType('custom')} style={[styles.segItem, { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, paddingVertical: 10, backgroundColor: payType==='custom' ? '#e0f2fe' : '#fff' }]}>
                  <Text style={{ fontFamily: 'Montserrat_600SemiBold', color: COLORS.text }}>
                    Custom amount
                  </Text>
                </TouchableOpacity>
                {payType === 'custom' && (
                  <TextInput
                    style={[styles.input, { marginTop: 8 }]}
                    keyboardType="numeric"
                    value={customAmount}
                    onChangeText={setCustomAmount}
                    placeholder="Enter amount in XAF"
                  />
                )}
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 12, marginTop: 16 }}>
              <View style={{ flex: 1 }}>
                <Button title="Cancel" onPress={() => setRepayVisible(false)} variant="tertiary" />
              </View>
              <View style={{ flex: 1 }}>
                <Button
                  title={submitting ? "Processing…" : "Pay now"}
                  onPress={initiateRepayment}
                  disabled={submitting || !loan}
                  variant="secondary"
                />
              </View>
            </View>

            <Text style={[styles.hint, { marginTop: 8 }]}>
              After you authorize the mobile money payment, your dashboard will refresh automatically.
            </Text>
          </View>
        </View>
      </Modal>

      {/* NEW: Logout confirmation modal */}
      <Modal transparent visible={logoutVisible} animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.modalSheet}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={styles.modalTitle}>Logout</Text>
              <TouchableOpacity onPress={() => setLogoutVisible(false)} style={{ padding: 4 }}>
                <Ionicons name="close" size={22} color={COLORS.muted} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.hint, { marginTop: 12 }]}>
              Are you sure you want to logout? You'll need to log in again to access your account.
            </Text>

            <View style={{ flexDirection: 'row', gap: 12, marginTop: 20 }}>
              <View style={{ flex: 1 }}>
                <Button 
                  title="Cancel" 
                  onPress={() => setLogoutVisible(false)} 
                  variant="tertiary" 
                />
              </View>
              <View style={{ flex: 1 }}>
                <Button
                  title="Logout"
                  onPress={handleLogout}
                  variant="secondary"
                />
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  )
}

// ---------- Helpers ----------
function LabeledInput({ label, ...props }) {
  return (
    <View style={{ marginTop: 12, width: "100%" }}>
      <Text style={styles.label}>{label}</Text>
      <TextInput style={styles.input} {...props} />
    </View>
  );
}

// Phone validation for Cameroon MTN & Orange
function normalizeCM(phone) {
  const digits = (phone || "").replace(/[^\d]/g, "");
  return digits.startsWith("237") ? digits.slice(3) : digits;
}
const MTN_PREFIXES = [650,651,652,653,654,655,656,657,658,659,670,671,672,673,674,675,676,677,678,679,680,681,682,683,684,685,686,687,688,689];
const ORANGE_PREFIXES = [690,691,692,693,694,695,696,697,698,699,656,657,658,659];
function validateCameroonPhone(phone, network) {
  const n = normalizeCM(phone);
  if (n.length !== 9) return "Enter a 9-digit number (e.g., 6XX XXX XXX)";
  if (!n.startsWith("6")) return "Cameroon mobile numbers start with 6";
  const prefix = parseInt(n.slice(0,3), 10);
  if (network === "MTN" && !MTN_PREFIXES.includes(prefix)) return "Not an MTN prefix";
  if (network === "Orange" && !ORANGE_PREFIXES.includes(prefix)) return "Not an Orange prefix";
  return "";
}

function AuthScreen({ onAuthed }) {
  const [mode, setMode] = useState('signin') // 'signin' | 'signup'
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function submit() {
    setLoading(true); setError('')
    try {
      const url = mode === 'signup' ? '/api/auth/register' : '/api/auth/login'
      const payload = mode === 'signup'
        ? { full_name: fullName.trim(), phone_e164: phone.trim(), password }
        : { phone_e164: phone.trim(), password }
      const { data } = await api.post(url, payload)
      if (!data?.token) throw new Error('No token returned')
      setAuthToken(data.token)
      onAuthed(data.user || null)
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Auth failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <SafeAreaView style={{ flex:1, backgroundColor: COLORS.bg }}>
      <View style={{ flex:1, alignItems:'center', justifyContent:'center', padding:16 }}>
        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>{mode==='signup' ? 'Create Account' : 'Sign In'}</Text>
          {mode==='signup' && (
            <LabeledInput label="Full name" value={fullName} onChangeText={setFullName} placeholder="Your legal name" />
          )}
          <LabeledInput label="Phone (E.164)" value={phone} onChangeText={setPhone} placeholder="+2376XXXXXXXX" />
          <LabeledInput label="Password" value={password} onChangeText={setPassword} placeholder="••••••••" secureTextEntry />
          {!!error && <Text style={styles.error}>{error}</Text>}
          <Button title={loading ? 'Please wait…' : (mode==='signup' ? 'Sign up' : 'Sign in')}
            onPress={submit} disabled={loading || (mode==='signup' && !fullName) || !phone || !password}
            style={{ marginTop: 12, width:'100%' }} />
          <TouchableOpacity onPress={() => setMode(mode==='signup' ? 'signin' : 'signup')} style={{ marginTop:12 }}>
            <Text style={[styles.hint, {textAlign:'center'}]}>
              {mode==='signup' ? 'Have an account? Sign in' : 'New here? Create an account'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  )
}


// ------------- Main App (simple screen state) --------------
export default function App() {
  const [fontsLoaded] = useFonts({
    Montserrat_400Regular, Montserrat_600SemiBold, Montserrat_700Bold
  });
  const [screen, setScreen] = useState("boot"); // 'boot' | 'home' | 'kyc' | 'awaiting' | 'dashboard'
  const [authed, setAuthed] = useState(false);
  const [profile, setProfile] = useState(null);

  const [showPrompt, setShowPrompt] = useState(false);
  const deferredPromptRef = useRef(null);

//   useEffect(() => {
//   (async () => {
//     // Load token (also attaches it to axios via your helper)
//     initAuthFromStorage();
//     const token = localStorage.getItem('token');

//     if (token) {
//       setAuthed(true);
//       setScreen('dashboard'); // ← go straight to dashboard
//     } else {
//       setAuthed(false);
//       setScreen('home');      // ← otherwise land on home
//     }
//   })();
// }, []);


    useEffect(() => {
      // 1) hydrate token + proactively drop if expired
      initAuthFromStorage()
      const token = (() => { try { return localStorage.getItem('token') } catch { return null } })()
      if (token) {
        setAuthed(true)
        setScreen('dashboard')
      } else {
        setAuthed(false)
        setScreen('home')
      }

      // 2) react to global logout events
      function onLogout() {
        setAuthed(false)
        setScreen('home')
      }
      window.addEventListener('auth:logout', onLogout)
      return () => window.removeEventListener('auth:logout', onLogout)
    }, [])


  const go = (s) => setScreen(s);

  useEffect(() => {
    if (Platform.OS === "web") {
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches || (navigator.standalone === true);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      let timer;
      function showSoon() {
        // Always show modal on web if not installed, to cover Android devices where event may not fire in dev.
        if (!isStandalone) setShowPrompt(true);
      }

      const onBIP = (e) => {
        e.preventDefault();
        deferredPromptRef.current = e;
        // Show quickly when we have the event
        if (!isStandalone) setShowPrompt(true);
      };

      window.addEventListener("beforeinstallprompt", onBIP);
      // Fallback: show after short delay (covers Android if event doesn't fire yet)
      timer = setTimeout(showSoon, 1200);

      return () => {
        window.removeEventListener("beforeinstallprompt", onBIP);
        if (timer) clearTimeout(timer);
      };
    }
  }, []);

  if (!fontsLoaded) return null;

    // When user taps "Get loan" on Home, send to auth if not authed
  function startFlow() {
    if (!authed) return setScreen('auth')
    setScreen('kyc')
  }


  // --- init token from storage (keeps user signed in on refresh if backend returns token) ---
  // useEffect(() => { initAuthFromStorage() }, [])

  return (
    <View style={{ flex: 1, backgroundColor: COLORS.white }}>
      <StatusBar barStyle="light-content" />
      {screen === "home" && <HomeScreen go={() => setScreen('kyc')} />}
      {screen === "kyc" && <KycScreen go={setScreen} setProfile={setProfile} />}
      {screen === "awaiting" && <AwaitingScreen go={setScreen} />}
      {screen === "awaiting_loan" && <AwaitingLoanScreen go={setScreen} />}
      {screen === "dashboard" && (
        <DashboardScreen 
          go={setScreen} 
          setAuthed={setAuthed}
          setScreen={setScreen}
        />
      )}
      {screen === "boot" && (
        <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
          <Text>Loading…</Text>
        </View>
      )}
      {Platform.OS === "web" && (
        <InstallPrompt
          visible={showPrompt}
          onClose={() => setShowPrompt(false)}
          deferredEvent={deferredPromptRef.current}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // HERO
  hero: {
    flex: 1,
    backgroundColor: COLORS.primary,
    justifyContent: "space-between",
    paddingTop: 48,
    paddingBottom: 28,
    paddingHorizontal: 20,
  },
  heroTop: {
    flexDirection: "row",
    alignItems: "center",
  },
  heroBrand: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 22,
    color: COLORS.white,
  },
  heroCenter: {
    alignItems: "center",
    paddingHorizontal: 8,
  },
  heroH1: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 32,
    color: COLORS.white,
    textAlign: "center",
  },
  heroSub: {
    fontFamily: "Montserrat_600SemiBold",
    fontSize: 18,
    color: COLORS.tertiary,
    marginTop: 8,
    textAlign: "center",
  },
  heroBottom: {
    paddingHorizontal: 4,
  },

  // Layout basics
  brand: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 22,
    color: COLORS.primary,
  },
  topBar: {
    width: "100%",
    maxWidth: 680,
    alignSelf: "center",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: COLORS.tertiary,
  },
  sectionTitle: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 20,
    color: COLORS.text,
    marginBottom: 8,
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: 16,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 2,
  },
  cardTitleLight: {
    fontFamily: "Montserrat_600SemiBold",
    color: "white",
    fontSize: 16,
    marginBottom: 8,
  },
  outstandingAmt: {
    fontFamily: "Montserrat_700Bold",
    color: "white",
    fontSize: 28,
    marginTop: 4,
  },

  // Centered form container
  centeredScroll: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    paddingBottom: 140
  },
  formCard: {
    width: "100%",
    maxWidth: 560,
    backgroundColor: COLORS.white,
    borderRadius: 16,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 10,
    elevation: 2,
  },

  // Inputs / Buttons / Segments
  label: {
    fontFamily: "Montserrat_600SemiBold",
    color: COLORS.text,
    marginBottom: 6,
  },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: "white",
    fontFamily: "Montserrat_400Regular",
  },
  button: {
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  buttonText: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 16,
  },
  hint: {
    fontFamily: "Montserrat_400Regular",
    color: COLORS.muted,
  },
  hintLight: {
    fontFamily: "Montserrat_400Regular",
    color: "#E5E7EB",
  },
  error: {
    marginTop: 6,
    color: "#B91C1C",
    fontFamily: "Montserrat_600SemiBold",
  },
  checkRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginTop: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: COLORS.tertiary,
    backgroundColor: "transparent",
  },
  segment: {
    flexDirection: "row",
    backgroundColor: "#E5E7EB",
    borderRadius: 12,
    padding: 4,
    marginBottom: 8,
    marginTop: 4,
  },
  segItem: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: "center",
  },
  segItemActive: {
    backgroundColor: COLORS.tertiary,
  },
  segText: {
    fontFamily: "Montserrat_600SemiBold",
    color: COLORS.muted,
  },
  segTextActive: {
    color: "#fff",
  },

  // Document upload button
  docButton: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#fff",
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  docButtonText: {
    fontFamily: "Montserrat_400Regular",
    color: COLORS.text,
  },
  docSmall: {
    fontFamily: "Montserrat_600SemiBold",
    color: COLORS.tertiary,
  },

  // Sticky bottom CTA (Dashboard)
  bottomCta: {
    position: "fixed",
    left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(255,255,255,0.92)",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    padding: 12,
    alignItems: "center",
  },

  // Modals
  modalBackdrop: {
    ...Platform.select({
      web: { position: "fixed" },
      default: { position: "absolute" }
    }),
    left: 0, right: 0, top: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalSheet: {
    width: "100%",
    maxWidth: 460,
    backgroundColor: "white",
    borderRadius: 16,
    padding: 16,
  },
  modalTitle: {
    fontFamily: "Montserrat_700Bold",
    fontSize: 18,
    color: COLORS.text,
  },
  modalText: {
    fontFamily: "Montserrat_400Regular",
    color: COLORS.text,
  },

  logoutButton: {
  padding: 8,
  borderRadius: 20,
  backgroundColor: '#f3f4f6',
},
});
